/* CDN URL is injected via webpack at build time */ 

export const SETTINGS = {
    cdnUrl: process.env.AZURE_CDN_URL ?? null
};

/* These constants reflect constants on the backend - considering injecting them at build time */

export const FORMAT_TYPES = {
    dollars: {
        noDecimal: '$0,0', 
        twoDecimals: '$0,0.00',
    },
    percent: {
        noDecimal: '0%',
        twoDecimals: '0.00%'
    },
    numbers: {
        noDecimal: '0,0',
        twoDecimals: '0.00'
    }
};

export const SSE_EVENTS = {
    PING: 'ping',
    START: 'start-presentation',
    END: 'end-presentation',
    CONNECTION: 'connection',
    SLIDE_ID: 'slide-id',
    PRESENTATION_TYPE: 'presentation-type',
    PDF_PAGE: 'pdf-page',
    SELECT_TERM: 'select-term',
    SHOW_POINTER: 'show-pointer',
    POINTER_POSITION: 'pointer-position',
    VIDEO: 'video-controls',
    LANGUAGE: 'update-language',
    STEP: 'step',
    INFO_ITEM: 'info-item',
    LOAN_INPUTS: 'loan-inputs',
    SCROLL: 'scroll'
};

export const ANALYTICS_EVENTS = {
    clientConnected: 'client_connected',
    loginDateAndTime: 'login_date_and_time',
    inviteSent: 'invite_sent_to_client', 
    pdfSent: 'pdf_sent', 
    slideStarted: 'slide_started', 
    slideEnded: 'slide_ended',
    slidesPresentationEvent: 'slides_presentation',
    documentsPresentationEvent: 'documents_presentation',
    slidesPresentationStarted: 'slides_presentation_started',
    slidesPresentationEnded: 'slides_presentation_ended',
    docsPresentationStarted: 'documents_presentation_started',
    docsPresentationEnded: 'documents_presentation_ended',
    sessionEnded: 'session_ended',
    script: 'script_opened'
};

export enum SLIDE_IDS {
    introduction = 1,
    yourAnalysis = 2,
    weCanHelp = 3,
    termOptions = 4,
    modification = 5,
    summary = 6,
    additionalInfo = 7,
    scProgram = 8,
    interestCalculator = 9,
    dti = 10,
    fico = 11,
    loanCalculator = 12,
    creditScore = 13,
    financialPath = 14,
    companyYouCanTrust = 15,
    clientTestimonials = 16,
    clientPortal = 17
};

export const DEFAULT_SEQUENCE = [
    { id: 1, isRequired: true, name: 'Introduction' },
    { id: 2, isRequired: true, name: 'Your Analysis' },
    { id: 3, isRequired: true, name: 'We Can Help' },
    { id: 4, isRequired: true, name: 'Term Options' },
    { id: 5, isRequired: true, name: 'Modification' },
    { id: 6, isRequired: true, name: 'Summary' },
    { id: 7, isRequired: true, name: 'Additional Information' }
];

const { fico, yourAnalysis, termOptions, summary, dti, financialPath, interestCalculator, loanCalculator } = SLIDE_IDS;

export const SLIDES_WITH_CREDIT_DATA = [fico, yourAnalysis, termOptions, summary, dti, financialPath, interestCalculator, loanCalculator];

 /* Quick helper for generating endpoints for the events (similarly done in the backend) */

 function getSseEndpoints(sseEvents) {
    const endpoints: { [key: string]: string } = {};

    for (let event in sseEvents) {
        if (sseEvents.hasOwnProperty(event)) {
            endpoints[event] = `/api/sse/${sseEvents[event]}`;
        }
    }

    endpoints.OPEN = '/api/sse/updates';

    return endpoints;
}

export const SSE_ENDPOINTS = getSseEndpoints(SSE_EVENTS);

/* These constants are frontend-specific */

export const BROWSER_STORAGE_KEYS = {
    clientConnectionKey: 'clientConnected',
    blobUrlKey: 'blobUrl',
    slideDimensionsKey: 'slideDimensions',
    viewportKey: 'clientViewport',
    slideIdKey: 'slideId',
    lastSlideIdKey: 'lastSlideId',
    selectedTermKey: 'selectedTerm',
    maxTermKey: 'maxTerm',
    stepsReviewedKey: 'stepsReviewed',
    loanCalculationsKey: 'loanCalculations',
    checkboxKey: 'checkboxItem',
    scrollPositionKey: 'scrollPosition',
    presentationTypeKey: 'presentationType',
    pdfPageKey: 'pdfPage',
    queryParamsKey: 'queryParams',
    scriptKey: 'script',
    pointerKey: 'pointer',
    presentationEndedKey: 'presentationEnded',
    firstSlideStartedKey: 'firstSlideStarted',
    presentationIdKey: 'presentationId'
};

export const QUERY_KEYS = {
    currentSlideQuery: 'currentSlide',
    clientQuery: 'client',
    userQuery: 'oneLoginUser',
    allSlidesQuery: 'allSlides',
    healthCheckQuery: 'healthCheck',
    rebuttalsQuery: 'rebuttals',
    slideSequenceQuery: 'slideSequence'
};

export const TIMEOUTS = {
    client: 60 * 60 * 1000,
    salesRep: 20 * 60 * 1000
};

export const PING_INTERVALS = {
    pointerMs: 35,
    oneSecond: 1000,
    twoSeconds: 2000,
    threeSeconds: 3000,
    fiveSeconds: 5000,
    oneMinute: 60000,
    fiveMinutes: 300000,
};

export const REBUTTAL_TAGS = {
    moreInformation: 'More Information',
    ficoAndCredit: 'Fico & Credit Surveys',
    VCL: 'VCL',
    video: 'Videos'
};

export enum Profile {
    ADMIN = 'Admin',
    TIMBERLINE = 'Timberline',
    STRATEGIC_CONSULTING = 'Strategic Consulting',
    ATLAS = 'Atlas',
    TESTING = 'Testing',
    CUSTOMER_SERVICE = 'Customer Service',
    SALES = 'Sales'
};

export type Language = 'English' | 'Spanish';

export const PAGE_URLS = {
    AUTHORIZE: '/',
    ADMIN_UPLOAD: '/admin/upload',
    HEALTH_CHECK: '/admin/healthcheck',
    ADMIN_PAGE: '/admin',
    CLIENT_LOGIN: '/client-login',
    CS_SLIDES: '/customer-service/:leadId',
    REP_SLIDES: '/slides/:leadId',
    CLIENT_SLIDES: '/presentation/:leadId',
    PDF_PAGE: '/pdf/:leadId/:slideSequence',
    DOCUMENTS_PAGE: '/documents/:leadId',
    TESTING_PAGE: '/testing',
    INVALID: '/invalid',
    NOT_AUTHORIZED: '/unauthorized',
    CLIENT_TIMEOUT: '/presentation-expired',
    REP_TIMEOUT: '/session-expired',
    NOT_FOUND: '/*',
    ADD_SLIDE: '/admin/add-slide'
};

export enum PresentationType {
    SLIDES_PRESENTATION = 'slides',
    DOCUMENTS_PRESENTATION = 'documents',
    CUSTOMER_SERVICE_PRESENTATION = 'customer-service'
}

export enum VIEWPORTS {
    mobile = 'mobile',
    tablet = 'tablet',
    desktop = 'desktop'
};

export const SHARED_STYLES = {
    desktopHeaderSize: '20px',
    mobileHeaderSize: '17px',
    primaryDarkBlue: '#02436D',
    secondaryDarkBlue: '#012939',
    primaryLightBlue: '#B8E1FF',
    secondaryLightBlue: '#E0F2FF',
    lightGrey: '#E5EBF0',
    darkGrey: '#E5E5E5',
    sansSerif: 'Noto Sans',
    serif: 'Noto Serif',
    smallFont: '13px',
    mediumFont: '14px',
    red: '#FF5959',
    green: '#5AEE56',
    maxSlideWidth: '768px',
    tabletWidth: '560px',
    mobileWidth: '480px'
};

export const TOAST_TYPES = {
    error: 'error',
    success: 'success',
    info: 'info',
    loading: 'loading',
    promise: 'promise'
};

export const STATIC_IMAGES = {
    certificate: 'Certificate.png',
    stepOne: 'Step-1.png',
    stepTwo: 'Step-2.png',
    stepThree: 'Step-3.png',
    stepFour: 'Step-4.png',
    sliderThumb: 'slider-thumb.png',
    creditScoreChart: 'credit-score-chart.png',
    ficoChart: 'fico-chart',
    pointer: 'mouse-pointer.svg',
    stratLogoLarge: 'Strat-logo_large.svg',
    trustPilotStars: 'TrustPilot_stars.svg'
};

export const BLOB_TYPES = {
    logo: 'logos',
    icon: 'icons',
    pdf: 'pdf',
    background: 'backgrounds',
    slide: 'slides'
};

export const FIRM_SHORT_NAMES = [
    'Royal', 
    'Great Lakes', 
    'Guidestone', 
    'Hallock & Associates', 
    'Monarch', 
    'Newport', 
    'North Star', 
    'Slate', 
    'Spring',
    'Timberline', 
    'White Oak', 
    'Stonepoint', 
    'Atlas',
    'Meadowbrook',
    'Infinite',
    'Hailstone',
    'Level One'
];

export enum Firm {
    ROYAL = 'Royal Legal Group',
    NEWPORT = 'Newport Legal Group',
    SPRING = 'Spring Legal Group',
    GUIDESTONE = 'Guidestone Law Group',
    GREEENSTONE = 'Greenstone Legal Group',
    HALLOCK = 'Hallock & Associates LLC',
    WHITE_OAK = 'White Oak Law Group',
    NORTHSTAR = 'Northstar Legal Group',   
    GREAT_LAKES = 'Great Lakes Law Firm',
    STONEPOINT = 'Stonepoint Legal Group',
    TIMBERLINE = 'Timberline Financial',
    ATLAS = 'Atlas Debt Relief',
    MEADOWBROOK = 'Meadowbrook Legal Group',
    INFINITE = 'Infinite Law Group',
    HAILSTONE = 'Hailstone Legal Group',
    LEVEL_ONE = 'Level One Law Group',
    CLEAR_CREEK = 'Clear Creek Legal Group',
    BRANDON_ELLIS = 'Brandon Ellis Law Firm LLC',
    CHARTER = 'Charter Legal Group',
    SANDSTONE = 'Sandstone Law Firm'
}

export const FIRM_FULL_NAMES = Object.values(Firm);

export const UNAVAILABLE_CS_FIRM = 'Unavailable';

export const SCROLL_IDS = {
    pdfBody: 'pdf-body',
    slideBody: 'slide-body',
    summaryTable: 'summary-table',
    creditorTable: 'creditor-table'
};

export const LOAN_INPUTS = {
    loanAmount: 'loanAmount',
    APR: 'APR',
    loanTerm: 'loanTerm',
    monthlyPayment: 'monthlyPayment'
};

export const EXCEPTION_KEY_CODES = {
    backspace: 8,
    arrowLeft: 37,
    arrowRight: 39,
    arrowUp: 38,
    arrowDown: 40,
    decimal: 190,
};

export const NUMBER_KEY_CODES = [48, 49, 50, 51, 52, 53, 54, 55, 56, 57];
